import React, { useMemo, useState } from "react";
import "./App.css";
import { SPOTS } from "./data/spots";
import type { FoodSpot } from "./types";

import Header from "./components/Header";
import Hero from "./components/Hero";
import Principles from "./components/Principles";
import Toolbar, { type FilterState, type ViewMode } from "./components/Toolbar";
import SpotList from "./components/SpotList";
import MapView from "./components/MapView";
import SpotDetail from "./components/SpotDetail";
import Footer from "./components/Footer";

const DEFAULT_FILTERS: FilterState = {
  query: "",
  cuisine: null,
  type: null,
  sort: "recommended",
};

function spotOrderIndex(spot: FoodSpot): number {
  return SPOTS.findIndex((s) => s.id === spot.id);
}

const App: React.FC = () => {
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [view, setView] = useState<ViewMode>("list");
  const [selectedSpot, setSelectedSpot] = useState<FoodSpot | null>(null);

  const filteredSpots = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    let result = SPOTS.filter((spot) => {
      if (filters.cuisine && spot.cuisine !== filters.cuisine) return false;
      if (filters.type && spot.type !== filters.type) return false;
      if (q) {
        const haystack = [
          spot.name,
          spot.area,
          spot.cuisine,
          spot.type,
          spot.description,
          ...spot.specialties,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });

    if (filters.sort === "rating") {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (filters.sort === "name") {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name));
    } else {
      result = [...result].sort(
        (a, b) => spotOrderIndex(a) - spotOrderIndex(b),
      );
    }

    return result;
  }, [filters]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  return (
    <div className="App" id="top">
      <Header count={SPOTS.length} />
      <Hero />
      <Principles />
      <Toolbar
        filters={filters}
        setFilters={setFilters}
        view={view}
        setView={setView}
        resultCount={filteredSpots.length}
      />
      <main className="main" id="results">
        <div className="results-meta">
          <span>
            {filteredSpots.length} {filteredSpots.length === 1 ? "spot" : "spots"}
            {(filters.cuisine || filters.type) && " filtered"}
          </span>
        </div>
        {view === "list" ? (
          <SpotList
            spots={filteredSpots}
            onSelect={setSelectedSpot}
            onClear={clearFilters}
          />
        ) : (
          <MapView
            spots={filteredSpots}
            onSelect={setSelectedSpot}
            selectedId={selectedSpot?.id ?? null}
          />
        )}
      </main>
      <Footer />
      <SpotDetail spot={selectedSpot} onClose={() => setSelectedSpot(null)} />
    </div>
  );
};

export default App;
